import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../../lib/apiClient';
import { getErrorMessage } from '../../lib/errors';
import { useRefreshOn } from '../../lib/resourceRefresh';
import type { ContentGoalsRead, ContentGoalsUpdate } from '../../types/api';
import { fetchContentGoals, saveContentGoals } from './calendarApi';

export interface ContentGoalsResult {
  /** Null means nobody has set goals for this month — not a failure. */
  goals: ContentGoalsRead | null;
  isLoading: boolean;
  /** Load error only. `save` throws so the form can keep the input on screen. */
  error: string | null;
  refresh: () => void;
  save: (body: ContentGoalsUpdate) => Promise<ContentGoalsRead>;
}

/** Result of the last completed load, tagged with what it was a load of. */
interface LoadedGoals {
  key: string;
  goals: ContentGoalsRead | null;
  error: string | null;
}

/**
 * Monthly content goals for one business.
 *
 * Same shape as `useCollection` and `useKbReadiness`: the load result is stored
 * tagged with the (business, month) it belongs to and the visible state is
 * derived during render, so nothing is written to state synchronously inside the
 * effect and last month's numbers can never flash under this month's heading.
 *
 * A 404 with code `RESOURCE_NOT_FOUND` is the API saying "no goals set", which
 * is a normal state of the screen, so it lands in `goals: null` rather than in
 * `error`. Any other failure is a real error.
 */
export function useContentGoals(businessId: string | null, month: string): ContentGoalsResult {
  const [loaded, setLoaded] = useState<LoadedGoals | null>(null);
  const [nonce, setNonce] = useState(0);

  const key = `${businessId ?? ''}:${month}`;

  const refresh = useCallback(() => setNonce((value) => value + 1), []);
  useRefreshOn(businessId, 'content_goals', refresh);

  useEffect(() => {
    if (!businessId) return;

    const controller = new AbortController();
    let cancelled = false;

    void (async () => {
      try {
        const goals = await fetchContentGoals(businessId, month, controller.signal);
        if (cancelled) return;
        setLoaded({ key: `${businessId}:${month}`, goals, error: null });
      } catch (caught) {
        if (cancelled || controller.signal.aborted) return;
        const unset = caught instanceof ApiError && caught.code === 'RESOURCE_NOT_FOUND';
        setLoaded({
          key: `${businessId}:${month}`,
          goals: null,
          error: unset ? null : getErrorMessage(caught, 'Could not read this month’s goals.'),
        });
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [businessId, month, nonce]);

  const save = useCallback(
    async (body: ContentGoalsUpdate): Promise<ContentGoalsRead> => {
      if (!businessId) throw new Error('No business context yet.');
      const saved = await saveContentGoals(businessId, month, body);
      setLoaded({ key: `${businessId}:${month}`, goals: saved, error: null });
      return saved;
    },
    [businessId, month],
  );

  const isCurrent = loaded !== null && loaded.key === key;

  return {
    goals: isCurrent ? loaded.goals : null,
    isLoading: businessId !== null && !isCurrent,
    error: isCurrent ? loaded.error : null,
    refresh,
    save,
  };
}
